import { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import { useCurrentUser } from "../utils/useCurrentUser";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function Profile() {
  const { empId, boId } = useCurrentUser();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!empId || !boId) return;
    setLoading(true);
    fetchProfileData();
  }, [empId, boId]);

  const fetchProfileData = async () => {
    try {
      const res = await fetch(`${BASE_URL}/EmployeeDetails/${boId}/${empId}`);
      if (!res.ok) throw new Error("Failed to fetch profile");
      const json = await res.json();
      setDetails(json);
      
      // Fetch profile picture with bo_id parameter
      const picRes = await fetch(`${BASE_URL}/api/users/${empId}/profile-picture?bo_id=${boId}`);
      if (picRes.ok) {
        const picData = await picRes.json();
        if (picData.success && picData.data?.profile_picture_url) {
          setProfilePicture(picData.data.profile_picture_url);
        }
      }
    } catch (err) {
      console.error("Profile fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type — SVG is intentionally excluded (security + sharp incompatibility)
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: `"${file.name}" is not a valid image. Please upload a JPG, PNG, GIF, or WebP file.`,
          severity: "error",
        });
        // Reset the input so the same file can be re-selected after fixing
        event.target.value = "";
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: "File size must be less than 5MB",
          severity: "error",
        });
        event.target.value = "";
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("profile_picture", selectedFile);

    try {
      const res = await fetch(
        `${BASE_URL}/api/users/${empId}/profile-picture?bo_id=${boId}`,
        { method: "POST", body: formData, credentials: "include" }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        // data URI returned directly — no BASE_URL prefix needed
        setProfilePicture(data.data.profile_picture_url);
        const channel = new BroadcastChannel("profile_picture");
        channel.postMessage({ type: "updated", url: data.data.profile_picture_url });
        channel.close();
        setSnackbar({ open: true, message: "Profile picture updated successfully!", severity: "success" });
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setPreviewUrl(null);
      } else {
        throw new Error(data.message || "Upload failed");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setSnackbar({ open: true, message: err.message || "Failed to upload profile picture", severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePicture = async () => {
    if (!window.confirm("Are you sure you want to remove your profile picture?")) {
      return;
    }

    try {
      const res = await fetch(
        `${BASE_URL}/api/users/${empId}/profile-picture?bo_id=${boId}`,
        {
          method: "DELETE",
          credentials: "include", // Uses HttpOnly cookie for authentication
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        setProfilePicture(null);
        // Notify Sidebar and other components to clear the profile picture
        const channel = new BroadcastChannel("profile_picture");
        channel.postMessage({ type: "deleted" });
        channel.close();
        setSnackbar({
          open: true,
          message: "Profile picture removed successfully!",
          severity: "success",
        });
      } else {
        throw new Error(data.message || "Failed to delete profile picture");
      }
    } catch (err) {
      console.error("Delete failed:", err);
      setSnackbar({
        open: true,
        message: "Failed to remove profile picture",
        severity: "error",
      });
    }
  };

  const handleCloseDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  if (!empId || !boId) {
    return <Typography>Loading user info…</Typography>;
  }

  if (loading) return <CircularProgress />;

  if (!details) return <Typography>No Profile Found</Typography>;

  const initials = `${details.first_name?.[0] ?? ""}${details.last_name?.[0] ?? ""}`.toUpperCase() || "U";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        mt: { xs: 2, sm: 4 },
        px: 2,
        pb: 6,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 600,
          borderRadius: 4,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Banner */}
        <Box
          sx={{
            height: { xs: 120, sm: 150 },
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            position: "relative",
          }}
        >
          {/* Avatar with Upload Button */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              bottom: -55,
              transform: "translateX(-50%)",
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={profilePicture || undefined}
                sx={{
                  width: { xs: 110, sm: 130 },
                  height: { xs: 110, sm: 130 },
                  border: "5px solid white",
                  bgcolor: "#667eea",
                  fontSize: { xs: 36, sm: 42 },
                  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                }}
              >
                {!profilePicture && initials}
              </Avatar>

              {/* Camera Icon Button */}
              <IconButton
                component="label"
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  bgcolor: "white",
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                  },
                }}
              >
                <CameraAltIcon sx={{ color: "#667eea" }} />
                <input
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                />
              </IconButton>

              {/* Delete Button (only if picture exists) */}
              {profilePicture && (
                <IconButton
                  onClick={handleDeletePicture}
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    bgcolor: "white",
                    boxShadow: 2,
                    "&:hover": {
                      bgcolor: "#ffebee",
                    },
                  }}
                >
                  <DeleteIcon sx={{ color: "#f44336" }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* Profile Info */}
        <Box sx={{ pt: 8, pb: 3, px: { xs: 2, sm: 4 } }}>
          {/* NAME */}
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              fontWeight: 600,
              mb: 0.5,
              fontSize: { xs: "1.3rem", sm: "1.6rem" },
            }}
          >
            {details.first_name} {details.last_name}
          </Typography>

          {/* DESIGNATION */}
          <Typography
            sx={{
              textAlign: "center",
              color: "text.secondary",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              mb: 3,
            }}
          >
            {details.designation || "—"}
          </Typography>

          {/* Info Section */}
          <Box>
            {[
              ["Email", details.bfx_email],
              ["Employee ID", details.emp_id],
              ["Mobile", details.mobile_1],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  py: 1.5,
                  borderBottom: "1px solid #eee",
                  textAlign: { xs: "center", sm: "left" },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    mb: { xs: 0.5, sm: 0 },
                  }}
                >
                  {label}
                </Typography>

                <Typography sx={{ fontWeight: 500, wordBreak: "break-word" }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Profile Picture</DialogTitle>
        <DialogContent>
          {previewUrl && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                my: 3,
              }}
            >
              <Avatar
                src={previewUrl}
                sx={{
                  width: 200,
                  height: 200,
                  boxShadow: 3,
                }}
              />
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" align="center">
            {selectedFile?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
            Maximum file size: 5MB • Supported formats: JPG, PNG, GIF, WebP (SVG not supported)
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
